import { ChangeEvent, FunctionComponent, RefObject, useState } from "react";
import { useTranslation } from "react-i18next";

import { faPaperclip, faPaperPlane, faXmark } from "@fortawesome/pro-regular-svg-icons";

import {
	TICKET_ATTACHMENT_ALLOWED_MIME_TYPES,
	TICKET_ATTACHMENT_MAX_FILES,
	TICKET_MESSAGE_MAX_LENGTH,
} from "@signalist/shared-types";

import { Button } from "@/components/Button/Button";
import { ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";
import {
	AttachmentRejection,
	formatFileSize,
	selectTicketAttachments,
	TICKET_ATTACHMENT_MAX_MB,
} from "@/components/SupportTicket/ticketAttachments";

import { ReplyOutcome, useTicketReply } from "../hooks/useTicketReply";

interface TicketReplyComposerProps {
	ticketId: number;
	/** Lets the resolved banner's "Not yet, reply" focus the textarea. */
	textareaRef?: RefObject<HTMLTextAreaElement>;
	onSent: (outcome: ReplyOutcome) => void;
}

/** The reply box under the thread (mockup `.composer`): text, the attach picker, "Send reply". */
export const TicketReplyComposer: FunctionComponent<TicketReplyComposerProps> = ({
	ticketId,
	textareaRef,
	onSent,
}) => {
	const { t } = useTranslation();
	const { send, isSending } = useTicketReply(ticketId);
	const [message, setMessage] = useState("");
	const [files, setFiles] = useState<File[]>([]);
	const [rejection, setRejection] = useState<AttachmentRejection | null>(null);

	const handlePick = (event: ChangeEvent<HTMLInputElement>) => {
		const selection = selectTicketAttachments(files, Array.from(event.target.files ?? []));

		setFiles(selection.files);
		setRejection(selection.rejection);
		// Let the same file be picked again after it was removed.
		event.target.value = "";
	};

	const handleRemove = (name: string) => {
		setFiles(files.filter((candidate) => candidate.name !== name));
		setRejection(null);
	};

	const handleSend = async () => {
		const outcome = await send(message.trim(), files);

		if (outcome.ok) {
			setMessage("");
			setFiles([]);
			setRejection(null);
		}

		onSent(outcome);
	};

	return (
		<div className="rounded-xl border border-gray-200 bg-white">
			<textarea
				ref={textareaRef}
				className="block w-full resize-none rounded-t-xl px-3.5 py-3 text-sm text-text outline-none placeholder:text-text-meta"
				maxLength={TICKET_MESSAGE_MAX_LENGTH}
				placeholder={t("support.detail.composer.placeholder")}
				rows={3}
				value={message}
				onChange={(event) => setMessage(event.target.value)}
			/>
			{files.length > 0 && (
				<div className="flex flex-wrap gap-2 px-3.5 pb-2">
					{files.map((file) => (
						<span
							key={file.name}
							className="inline-flex h-8 items-center gap-2 rounded-lg border border-gray-200 px-2.5 text-xs font-medium text-text"
						>
							{file.name}
							<span className="text-text-meta">{formatFileSize(file.size)}</span>
							<button
								aria-label={t("support.modal.remove", { name: file.name })}
								className="cursor-pointer text-text-meta hover:text-text"
								type="button"
								onClick={() => handleRemove(file.name)}
							>
								<Icon icon={faXmark} />
							</button>
						</span>
					))}
				</div>
			)}
			{rejection && (
				<p className="px-3.5 pb-2 text-xs text-status-error">
					{t(`support.modal.errors.${rejection.key}`, rejection.params)}
				</p>
			)}
			<div className="flex items-center gap-3 border-t border-gray-200 px-3 py-2">
				<label className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg px-2 text-xs font-semibold text-text-meta transition-colors duration-200 hover:bg-status-info-light hover:text-text">
					<Icon icon={faPaperclip} />
					{t("support.detail.composer.attach")}
					<input
						accept={TICKET_ATTACHMENT_ALLOWED_MIME_TYPES.join(",")}
						aria-label={t("support.detail.composer.attach")}
						className="hidden"
						multiple
						type="file"
						onChange={handlePick}
					/>
				</label>
				<span className="text-xs text-text-meta">
					{t("support.detail.composer.hint", {
						maxFiles: TICKET_ATTACHMENT_MAX_FILES,
						maxMb: TICKET_ATTACHMENT_MAX_MB,
					})}
				</span>
				<div className="flex-1" />
				<Button
					disabled={message.trim().length === 0 || isSending}
					image={<Icon className="mr-1.5" icon={faPaperPlane} />}
					isLoading={isSending}
					size={ButtonSize.XS}
					title={t("support.detail.composer.send")}
					type="button"
					onClick={() => void handleSend()}
				/>
			</div>
		</div>
	);
};
